import React, { useEffect, useState } from "react";
import { Box, Typography, Button, IconButton, Tooltip, useMediaQuery } from "@mui/material";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import sh from "bundles/common/utils/sh";
import CandidateDialog from "bundles/common/components/CandidateDialog";
import trackEvent from "bundles/common/utils/trackEvent";
import ContactCandidateModal from "bundles/common/components/ContactCandidateModal";
import CandidateNoteModal from "bundles/common/components/CandidateNoteModal";
import CandidateSummaryBlock from "./CandidateSummaryBlock";
import CvTextExcerptBlock from "./CvTextExcerptBlock";
import CandidateSkillsBlock from "./CandidateSkillsBlock";
import AddToKanbanModal from "./AddToKanbanModal";
import { useTheme } from "@mui/styles";

const CVThequeCandidateDetail = ({
  selectedCandidate,
  setSelectedCandidate,
  resumeLibraryCredit,
  setKanban,
  scrollToTopOnCandidateChange = false,
  onClose = null,
}) => {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [addToKanbanModalOpen, setAddToKanbanModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteCount, setNoteCount] = useState(0);

  // Badge on the header note icon: how many notes this company already wrote on
  // the candidate (same CandidateNote records as the Kanban dialog notes).
  useEffect(() => {
    if (!resumeLibraryCredit || !selectedCandidate?.id) {
      setNoteCount(0);
      return undefined;
    }

    let cancelled = false;
    sh.get("candidate_notes", {
      // Own cancel key: sh cancels by URL, which the note modal also requests.
      cancelKey: "cvtheque-candidate-note-count",
      params: { record_type: "Candidate", record_id: selectedCandidate.id },
    })
      .then((res) => {
        if (!cancelled) setNoteCount((res.data?.data || []).length);
      })
      .catch(() => {
        if (!cancelled) setNoteCount(0);
      });

    return () => {
      cancelled = true;
    };
  }, [resumeLibraryCredit, selectedCandidate?.id]);


  const closeDialog = () => {
    setSelectedCandidate(null);
  };

  const handleAddToKanbanSuccess = (responseData) => {
    setAddToKanbanModalOpen(false);
    
    if (responseData?.kanban_card && typeof setKanban === "function") {
      const newCard = responseData.kanban_card;
      const columnId = String(newCard.kanban_column_id);
      
      setKanban((prev) => {
        const prevCards = prev?.cards_by_column_id || {};
        const existingCards = prevCards[columnId] || [];
        
        const cardExists = existingCards.some((c) => c.id === newCard.id);
        if (cardExists) {
          return {
            ...prev,
            cards_by_column_id: {
              ...prevCards,
              [columnId]: existingCards.map((c) =>
                c.id === newCard.id ? { ...c, ...newCard } : c
              ),
            },
          };
        }
        
        return {
          ...prev,
          cards_by_column_id: {
            ...prevCards,
            [columnId]: [newCard, ...existingCards],
          },
        };
      });
    }
  };

  if (resumeLibraryCredit) {
    if (!selectedCandidate) return null;

    return(
      <>
        <CandidateDialog
          candidate={selectedCandidate}
          jobApplication={null}
          isDialogOpen={!isMobile}
          setIsDialogOpen={closeDialog}
          onClose={closeDialog}
          columns={[]}
          currentColumnId={null}
          onMoveToColumn={null}
          isModal={isMobile}
          enableCandidateReveal={true}
          hideNotes={true}
          onAddNote={() => setNoteModalOpen(true)}
          noteCount={noteCount}
          showContactButton={false}
          scrollToTopOnCandidateChange={scrollToTopOnCandidateChange}
          onPanelClose={onClose}
        />
        <CandidateSummaryBlock candidate={selectedCandidate} />
        <CvTextExcerptBlock candidate={selectedCandidate} />
        <CandidateSkillsBlock candidate={selectedCandidate} />
        
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            onClick={() => {
              setAddToKanbanModalOpen(true);
              trackEvent("cvtheque", "add_to_kanban_opened", { candidate_id: selectedCandidate?.id });
            }}
            sx={{
              width: "100%",
              height: "42px",
              borderRadius: "29px",
              backgroundColor: theme.palette.text.main,
              color: theme.palette.common.white,
              fontFamily: "Segoe UI Semibold, sans-serif",
              textTransform: "none",
              "&:hover": {
                backgroundColor: theme.palette.text.dark,
                color: theme.palette.common.white,
              },
            }}
          >
            <FormattedMessage id="recruiter.cv_theque.add_to_kanban" />
          </Button>
        </Box>

        <Box display="flex" justifyContent="center" alignItems="center" gap={1.5} mt={1.5}>
          <Button
            onClick={() => setContactModalOpen(true)}
            disableRipple
            sx={{
              flex: 1,
              minWidth: 0,
              height: "42px",
              borderRadius: "29px",
              backgroundColor: theme.palette.common.white,
              color: theme.palette.text.main,
              border: `1px solid ${theme.palette.text.main}`,
              fontFamily: "Segoe UI Semibold, sans-serif",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "hsla(153, 68%, 25%, 0.06)",
                borderColor: "hsl(153, 68%, 25%)",
              },
            }}
          >
            <FormattedMessage id="recruiter.messaging.contact_candidate" />
          </Button>

          {/* On mobile the CandidateDialog (and its header note icon) is not rendered. */}
          {isMobile && (
            <Tooltip
              title={intl.formatMessage({ id: "recruiter.kanban.add_note", defaultMessage: "Add private note" })}
              placement="top"
              arrow
            >
              <IconButton
                onClick={() => setNoteModalOpen(true)}
                aria-label={intl.formatMessage({ id: "recruiter.kanban.add_note", defaultMessage: "Add private note" })}
                disableRipple
                sx={{
                  flexShrink: 0,
                  width: "42px",
                  height: "42px",
                  backgroundColor: theme.palette.common.white,
                  color: theme.palette.text.main,
                  border: `1px solid ${theme.palette.text.main}`,
                  "&:hover": {
                    backgroundColor: "hsla(153, 68%, 25%, 0.06)",
                    borderColor: "hsl(153, 68%, 25%)",
                  },
                }}
              >
                <NoteAddOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

        </Box>

        <AddToKanbanModal
          open={addToKanbanModalOpen}
          onClose={() => setAddToKanbanModalOpen(false)}
          candidate={selectedCandidate}
          onSuccess={handleAddToKanbanSuccess}
        />

        <ContactCandidateModal
          open={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          candidate={selectedCandidate}
        />

        <CandidateNoteModal
          open={noteModalOpen}
          onClose={() => setNoteModalOpen(false)}
          candidate={selectedCandidate}
          onNoteCreated={() => setNoteCount((prev) => prev + 1)}
        />

      </>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="300px"
    >
      <Typography variant="h5" textAlign="center" mb={2}>
        <FormattedMessage id="credits_box.no_cv_access" />
      </Typography>
      <Button
        variant="rounded"
        color="primaryContained"
        href={`/${i18nLocale}/recruiter/pricing`}
      >
        <Typography variant="h6">
          <b>
            <FormattedMessage id="order.order" />
          </b>
        </Typography>
      </Button>
    </Box>
  );
};

export default CVThequeCandidateDetail;
